import { ExclamationIcon } from "@heroicons/react/outline"
import { useTranslation } from "react-i18next"
import { getFriendlyErrorMessage } from "./AssignOrdersGroupableIndicator"

// Full class literals per tone so Tailwind's purge keeps them.
const TONES = {
  red: { box: "border-red-300 bg-red-50", head: "text-red-700", item: "text-red-800", status: "text-red-500", code: "bg-red-100", friendly: "text-red-700", detail: "text-red-400" },
  amber: { box: "border-amber-300 bg-amber-50", head: "text-amber-700", item: "text-amber-800", status: "text-amber-600", code: "bg-amber-100", friendly: "text-amber-700", detail: "text-amber-500" },
}

// Surfaces an `errors`/`excluded` array (from POST /picking-order or
// /picking-order/preview) so operators can read each failing/excluded order.
// Shows a human-friendly (localized) explanation when the error code is known
// (e.g. invalid_order_status → "no está en estado 'Pendiente'..."), keeping the
// raw code + detail as secondary text to report to tech.
// - `title` overrides the default "No se pudieron procesar N pedidos:" header.
// - `tone` = "red" (blocking error) | "amber" (non-blocking, e.g. excluded).
// - `scrollable` caps the list height and scrolls internally. Use it where the
//   panel sits in a non-scrolling area (e.g. the legacy AssignOrdersDialog footer,
//   above the action buttons); leave it off inside an already-scrolling dialog body
//   to avoid a nested scrollbar.
export const PickingOrderErrorsPanel = ({ errors = [], className = "", title = null, tone = "red", scrollable = false }) => {
  const { i18n } = useTranslation()
  if (!errors || errors.length === 0) return null
  const t = TONES[tone] || TONES.red
  return (
    <div className={`rounded-lg border ${t.box} p-3 text-sm ${className}`}>
      <div className={`flex items-center gap-2 ${t.head} font-medium mb-2`}>
        <ExclamationIcon className="h-5 w-5 flex-shrink-0" />
        <span>
          {title || i18n.t("picking_tasks.order_errors.panel_title", { count: errors.length })}
        </span>
      </div>
      <ul className={`space-y-3 ${scrollable ? "max-h-72 overflow-y-auto" : ""}`}>
        {errors.map((e, i) => {
          const statusLabel = e.order?.status
            ? i18n.t(`statuses.order_statuses.${e.order.status}`, { defaultValue: e.order.status })
            : null
          // For invalid_order_status, spell out the order's current status inline
          // (the backend gives us order.status). Other codes use the shared map.
          let friendly = getFriendlyErrorMessage(e)
          if (e.error_type === "invalid_order_status" && statusLabel) {
            friendly = i18n.t("picking_tasks.order_errors.invalid_order_status_with_status", { status: statusLabel })
          }
          // The status is already in the message for invalid_order_status, so
          // only show the separate metadata line for the other codes.
          const showStatusLine = statusLabel && e.error_type !== "invalid_order_status"
          return (
            <li key={`${e.order?.id ?? i}-${e.error_type ?? i}`} className={`${t.item} ${i > 0 ? "pt-3 border-t border-gray-200" : ""}`}>
              <div className="font-semibold">#{e.order?.order_number ?? e.order?.id ?? "—"}</div>
              {/* Prefer our localized message. Fall back to the backend's raw
                  `detail` (often English) only when the code is unknown, so we
                  never leave the operator without an explanation. */}
              {friendly
                ? <div className={`text-xs ${t.friendly} mt-0.5`}>{friendly}</div>
                : e.detail && <div className={`text-xs ${t.friendly} mt-0.5 break-words`}>{e.detail}</div>}
              {showStatusLine && (
                <div className="text-xs text-gray-500 mt-1">
                  {i18n.t("picking_tasks.order_errors.order_status_label")} <span className={t.status}>{statusLabel}</span>
                </div>
              )}
              {e.error_type && (
                <div className="text-xs text-gray-500 mt-0.5" title={i18n.t("picking_tasks.order_errors.code_title")}>
                  {i18n.t("picking_tasks.order_errors.code_label")} <span className={`font-mono ${t.code} rounded px-1 py-0.5`}>{e.error_type}</span>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
